/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
import { initAxios } from '../utils/axiosHelper';
import { parseErrorMsg } from '../utils/errorHelper';
import { IScreeningResultRow } from '../interfaces/IBiteDB';

export const BiteDBAPI = {
  getDatabaseEmailAudience: async (databaseId: string) => {
    const apiClient = await initAxios();
    if (!apiClient) {
      return { success: false, result: 'Error initializing api client' };
    }
    return apiClient
      .get(`/bite-db/database/${databaseId}/email-audience`)
      .then((resp) => ({
        success: true,
        result: resp.data.data,
      }))
      .catch((err) => ({
        success: false,
        result: parseErrorMsg(err),
      }));
  },
  saveDatabaseEmailAudience: async (
    databaseId: string,
    payload: {
      expected_version: number;
      mode: 'all_team' | 'selected';
      team_member_ids: string[];
      external_emails?: string[];
    },
  ) => {
    const apiClient = await initAxios();
    if (!apiClient) {
      return { success: false, result: 'Error initializing api client', status: 0 };
    }
    return apiClient
      .put(`/bite-db/database/${databaseId}/email-audience`, payload)
      .then((resp) => ({
        success: true,
        result: resp.data.data,
        status: resp.status,
      }))
      .catch((err) => ({
        success: false,
        result: parseErrorMsg(err),
        status: err?.response?.status ?? 0,
      }));
  },
  bulkUploadSuppliers: async (
    formData: FormData,
    groupId?: string,
  ): Promise<{ success: boolean; result?: any; data?: string }> => {
    try {
      const apiClient = await initAxios();
      if (!apiClient) {
        return {
          success: false,
          data: 'Error initializing api client',
        };
      }

      delete apiClient.defaults.headers.common['Content-Type'];
      delete apiClient.defaults.headers.post['Content-Type'];
      delete apiClient.defaults.headers['Content-Type'];

      const response = await apiClient.post(
        '/bite-db/add-supplier-bulk-upload',
        formData,
        {
          headers: {
            'x-group-id': groupId || '',
          },
        },
      );

      return {
        success: true,
        result: response.data,
      };
    } catch (error) {
      return {
        success: false,
        result: parseErrorMsg(error),
      };
    }
  },
  bulkUploadTariffs: async (
    formData: FormData,
    groupId?: string,
  ): Promise<{ success: boolean; result?: any; data?: string }> => {
    try {
      const apiClient = await initAxios();
      if (!apiClient) {
        return {
          success: false,
          data: 'Error initializing api client',
        };
      }

      delete apiClient.defaults.headers.common['Content-Type'];
      delete apiClient.defaults.headers.post['Content-Type'];
      delete apiClient.defaults.headers['Content-Type'];

      const response = await apiClient.post(
        '/bite-db/add-tariff-bulk-upload',
        formData,
        {
          headers: {
            'x-group-id': groupId || '',
          },
        },
      );

      return {
        success: true,
        result: response.data,
      };
    } catch (error) {
      return {
        success: false,
        result: parseErrorMsg(error),
      };
    }
  },
  createDatabase: async (database: any) =>
    (await initAxios())
      ?.post('/bite-db/database', JSON.stringify(database))
      .then((resp) => {
        return {
          success: true,
          result: resp.data,
        };
      })
      .catch((err) => {
        let message = '';
        const main_message =
          err.response?.data?.error?.message ||
          err.response?.data?.message ||
          err.message ||
          'Unknown error';

        const nestedErrors = err.response?.data?.error?.errors
          ? Object.values(err.response.data.error.errors).flat().join(' ')
          : '';

        message = [main_message, nestedErrors].filter(Boolean).join(' - ');

        return {
          success: false,
          result: message,
        };
      }),
  getUserDatabases: async (id: string | undefined) =>
    (await initAxios())
      ?.get(`/bite-db/database/${id}`)
      .then((resp) => {
        return {
          success: true,
          result: resp.data,
        };
      })
      .catch((err) => {
        return {
          success: false,
          result: parseErrorMsg(err),
        };
      }),
  getDatabasesByGroupId: async (id: string | undefined) =>
    (await initAxios())
      ?.get(`/bite-db/database-group/${id}`)
      .then((resp) => {
        return {
          success: true,
          result: resp.data.data,
        };
      })
      .catch((err) => {
        return {
          success: false,
          result: parseErrorMsg(err),
        };
      }),
  searchDatabasesByGroupId: async (searchData: any) =>
    (await initAxios())
      ?.post('/bite-db/databases/search', JSON.stringify(searchData))
      .then((resp) => {
        return {
          success: true,
          result: resp.data.data,
        };
      })
      .catch((err) => {
        return {
          success: false,
          result: parseErrorMsg(err),
        };
      }),

  deleteDatabase: async (id: string | undefined) =>
    (await initAxios())
      ?.delete(`/bite-db/database/${id}`)
      .then((resp) => {
        return {
          success: true,
          result: resp.data.data,
        };
      })
      .catch((err) => {
        return {
          success: false,
          result: parseErrorMsg(err),
        };
      }),
  addSupplierToDatabase: async (database: any, groupId?: string) =>
    (await initAxios())
      ?.post('/bite-db/add-supplier', JSON.stringify(database), {
        headers: {
          'x-group-id': groupId || '',
        },
      })
      .then((resp) => {
        return {
          success: true,
          result: resp.data,
        };
      })
      .catch((err) => {
        return {
          success: false,
          result: parseErrorMsg(err),
        };
      }),

  /**
   * Create a new product
   * @param {any} product - The product data to create
   * @returns {Promise<{success: boolean, result: any}>} Object containing success status and result data
   */
  createProduct: async (product: any, teamId: any) =>
    (await initAxios())
      ?.post('/bite-db/product', JSON.stringify(product), {
        headers: {
          'x-group-id': teamId || '',
        },
      })
      .then((resp) => {
        return {
          success: true,
          result: resp.data,
        };
      })
      .catch((err) => {
        return {
          success: false,
          result: parseErrorMsg(err),
        };
      }),

  /**
   * Bulk upload products using a CSV file
   * @param {FormData} formData - The form data containing the file and metadata
   * @returns {Promise<{success: boolean, result: any}>} Object containing success status and result data
   */
  bulkUploadProducts: async (
    formData: FormData,
  ): Promise<{ success: boolean; result?: any; data?: string }> => {
    try {
      const apiClient = await initAxios();
      if (!apiClient) {
        return {
          success: false,
          data: 'Error initializing api client',
        };
      }

      delete apiClient.defaults.headers.common['Content-Type'];
      delete apiClient.defaults.headers.post['Content-Type'];
      delete apiClient.defaults.headers['Content-Type'];

      const response = await apiClient.post(
        '/bite-db/add-product-bulk-upload',
        formData,
        { headers: {} },
      );

      return {
        success: true,
        result: response.data,
      };
    } catch (error) {
      return {
        success: false,
        result: parseErrorMsg(error),
      };
    }
  },
  getEntitiesByDatabase: async (id: string | undefined) =>
    (await initAxios())
      ?.get(`/bite-db/entities/database/${id}`)
      .then((resp) => {
        return {
          success: true,
          result: resp.data.data,
        };
      })
      .catch((err) => {
        return {
          success: false,
          result: parseErrorMsg(err),
        };
      }),
  updateProductStatusByID: async (id: string, productData: any) =>
    (await initAxios())
      ?.put(`/bite-db/product/${id}`, JSON.stringify(productData))
      .then((resp) => {
        return {
          success: true,
          result: resp.data.data,
        };
      })
      .catch((err) => {
        return {
          success: false,
          result: parseErrorMsg(err),
        };
      }),

  /**
   * Get products by database ID
   * @param {string} id - The ID of the database to get products for
   * @returns {Promise<{success: boolean, result: any}>} Object containing success status and result data
   */
  getProductsByDatabase: async (id: string | undefined) =>
    (await initAxios())
      ?.get(`/bite-db/products/database-id/${id}`)
      .then((resp) => {
        return {
          success: true,
          result: resp.data.data,
        };
      })
      .catch((err) => {
        return {
          success: false,
          result: parseErrorMsg(err),
        };
      }),
  getAllEntitiesByGroupID: async () =>
    (await initAxios())
      ?.get('/bite-db/entities')
      .then((resp) => {
        return {
          success: true,
          result: resp.data.data,
        };
      })
      .catch((err) => {
        return {
          success: false,
          result: parseErrorMsg(err),
        };
      }),

  /**
   * Get all entities by group ID
   * @param {string} groupId - The ID of the group to get entities for
   * @returns {Promise<{success: boolean, result: any}>} Object containing success status and result data
   */
  getEntitiesByGroupId: async (groupId: string) =>
    (await initAxios())
      ?.get(`/bite-db/entities/group/${groupId}`)
      .then((resp) => {
        return {
          success: true,
          result: resp.data.data,
        };
      })
      .catch((err) => {
        return {
          success: false,
          result: parseErrorMsg(err),
        };
      }),

  /**
   * Get all products by group ID
   * @param {string} groupId - The ID of the group to get products for
   * @returns {Promise<{success: boolean, result: any}>} Object containing success status and result data
   */
  getProductsByGroupId: async (groupId: string) =>
    (await initAxios())
      ?.get(`/bite-db/products/database/${groupId}`)
      .then((resp) => {
        return {
          success: true,
          result: resp.data.data,
        };
      })
      .catch((err) => {
        return {
          success: false,
          result: parseErrorMsg(err),
        };
      }),
  updateDatabases: async (database: any) =>
    (await initAxios())
      ?.post('/bite-db/multi-databases', JSON.stringify(database))
      .then((resp) => {
        return {
          success: true,
          result: resp.data,
        };
      })
      .catch((err) => {
        let message = '';
        const main_message =
          err.response?.data?.error?.message ||
          err.response?.data?.message ||
          err.message ||
          'Unknown error';

        const nestedErrors = err.response?.data?.error?.errors
          ? Object.values(err.response.data.error.errors).flat().join(' ')
          : '';

        message = [main_message, nestedErrors].filter(Boolean).join(' - ');

        return {
          success: false,
          result: message,
        };
      }),

  /**
   * Get number of items - returns number of people, companies, and products across all databases for a group.
   * @param {string} [groupId] - The ID of the group to get item counts for
   * @returns {Promise<{success: boolean, result: any}>} Object containing success status and result data
   */
  getNrOfItems: async (groupId?: string): Promise<any> =>
    (await initAxios())
      ?.get(`/bite-db/total-items/${groupId}`)
      .then((resp) => {
        return {
          success: true,
          result: resp.data,
        };
      })
      .catch((err) => {
        return {
          success: false,
          result: parseErrorMsg(err),
        };
      }),
  runDatabaseScreening: async (database_id: string | number[]) =>
    (await initAxios('/automations'))
      ?.post('screenings/run', {
        databaseIds: database_id,
      })
      .then((resp) => {
        return {
          success: true,
          result: resp.data,
        };
      })
      .catch((err) => {
        return {
          success: false,
          result: parseErrorMsg(err),
        };
      }),
  runProductDatabaseScreenings: async (database_id: string | string[]) =>
    (await initAxios('/automations'))
      ?.post('screenings/run/products', {
        databaseIds: database_id,
      })
      .then((resp) => {
        return {
          success: true,
          result: resp.data,
        };
      })
      .catch((err) => {
        return {
          success: false,
          result: parseErrorMsg(err),
        };
      }),
  getScreeningResultsForGroup: async (
    groupId: string,
  ): Promise<{ success: boolean; result: IScreeningResultRow[] } | undefined> =>
    (await initAxios())
      ?.get(`bite-db/screening-history-group/${groupId}`)
      .then((resp) => {
        return {
          success: true,
          result: resp.data.data,
        };
      })
      .catch((err) => {
        return {
          success: false,
          result: parseErrorMsg(err),
        };
      }),
  fetchScreeningResultsByDbId: async (
    id: string,
  ): Promise<{ success: boolean; result: IScreeningResultRow[] } | undefined> =>
    (await initAxios())
      ?.get(`bite-db/screening-history/database/${id}`)
      .then((resp) => {
        return {
          success: true,
          result: resp.data.data,
        };
      })
      .catch((err) => {
        return {
          success: false,
          result: parseErrorMsg(err),
        };
      }),
  fetchEntityScreening: async (screeningData: any) =>
    (await initAxios())
      ?.post('/bite-db/screen-histories', JSON.stringify(screeningData))
      .then((resp) => {
        return {
          success: true,
          result: resp.data,
        };
      })
      .catch((err) => {
        return {
          success: false,
          result: parseErrorMsg(err),
        };
      }),

  /**
   * Get suppliers (entities) linked to a specific product
   * @param {string} productId - The ID of the product to get suppliers for
   * @returns {Promise<{success: boolean, result: any}>} Object containing success status and result data
   */
  getSuppliersByProductId: async (productId: string) =>
    (await initAxios())
      ?.get(`/bite-db/supplier-products/product/${productId}`)
      .then((resp) => {
        return {
          success: true,
          result: resp.data.data,
        };
      })
      .catch((err) => {
        return {
          success: false,
          result: parseErrorMsg(err),
        };
      }),

  /**
   * Get products linked to a specific supplier (entity)
   * @param {string} supplierId - The ID of the supplier to get products for
   * @returns {Promise<{success: boolean, result: any}>} Object containing success status and result data
   */
  getProductsBySupplierId: async (supplierId: string) =>
    (await initAxios())
      ?.get(`/bite-db/supplier-products/supplier/${supplierId}`)
      .then((resp) => {
        return {
          success: true,
          result: resp.data.data,
        };
      })
      .catch((err) => {
        return {
          success: false,
          result: parseErrorMsg(err),
        };
      }),

  /**
   * Create bulk supplier-product relationships
   * @param {Array} relationships - Array of supplier-product relationships to create
   * @returns {Promise<{success: boolean, result: any}>} Object containing success status and result data
   */
  createBulkSupplierProducts: async (
    relationships: Array<{
      supplier_id?: string;
      product_id?: string;
      price?: number;
    }>,
  ) =>
    (await initAxios())
      ?.post(
        '/bite-db/supplier-products/bulk-link',
        JSON.stringify({ relationships: relationships }),
      )
      .then((resp) => {
        return {
          success: true,
          result: resp.data.data,
        };
      })
      .catch((err) => {
        return {
          success: false,
          result: parseErrorMsg(err),
        };
      }),
  removeProductSupplierLink: async (id: string) =>
    (await initAxios())
      ?.delete(`/bite-db/supplier-product/${id}`)
      .then((resp) => {
        return {
          success: true,
          result: resp.data.data,
        };
      })
      .catch((err) => {
        return {
          success: false,
          result: parseErrorMsg(err),
        };
      }),

  toggleEntityDocVisibility: async (parentId: string, itemId: string) => {
    try {
      const apiClient = await initAxios();
      if (!apiClient) {
        return {
          success: false,
          data: 'Error initializing api client',
        };
      }
      const reqBody = {
        parent_id: parentId,
        item_id: itemId,
      };

      const response = await apiClient.post(
        '/bite-db/toggle-entity-doc-visibility',
        reqBody,
      );

      return {
        success: true,
        result: response.data,
      };
    } catch (error) {
      return {
        success: false,
        result: parseErrorMsg(error),
      };
    }
  },

  toggleProductDocVisibility: async (parentId: string, itemId: string) => {
    try {
      const apiClient = await initAxios();
      const reqBody = {
        parent_id: parentId,
        item_id: itemId,
      };
      if (!apiClient) {
        return {
          success: false,
          data: 'Error initializing api client',
        };
      }

      const response = await apiClient.post(
        '/bite-db/toggle-product-doc-visibility',
        reqBody,
      );

      return {
        success: true,
        result: response.data,
      };
    } catch (error) {
      return {
        success: false,
        result: parseErrorMsg(error),
      };
    }
  },
  getDatabaseById: async (id: string) =>
    (await initAxios())
      ?.get(`/bite-db/database/${id}`)
      .then((resp) => {
        return {
          success: true,
          result: resp.data,
        };
      })
      .catch((err) => {
        return {
          success: false,
          result: parseErrorMsg(err),
        };
      }),

  // Comment operations
  /**
   * Create a new comment for an entity or product
   * @param {any} commentData - The comment data to create
   * @returns {Promise<{success: boolean, result: any}>} Object containing success status and result data
   */
  createComment: async (commentData: any) =>
    (await initAxios())
      ?.post('/bite-db/comment', JSON.stringify(commentData))
      .then((resp) => {
        return {
          success: true,
          result: resp.data,
        };
      })
      .catch((err) => {
        return {
          success: false,
          result: parseErrorMsg(err),
        };
      }),

  /**
   * Get a comment by ID
   * @param {string} id - The ID of the comment to retrieve
   * @returns {Promise<{success: boolean, result: any}>} Object containing success status and result data
   */
  getCommentById: async (id: string) =>
    (await initAxios())
      ?.get(`/bite-db/comment/${id}`)
      .then((resp) => {
        return {
          success: true,
          result: resp.data,
        };
      })
      .catch((err) => {
        return {
          success: false,
          result: parseErrorMsg(err),
        };
      }),

  /**
   * Get comments for an entity or product
   * @param {string} commentableId - The ID of the entity or product
   * @param {string} commentableType - The type of the commentable object (DatabaseEntity or DatabaseProduct)
   * @returns {Promise<{success: boolean, result: any}>} Object containing success status and result data
   */
  getCommentsByCommentableId: async (
    commentableId: string,
    commentableType?: string,
  ) => {
    const queryParams = commentableType
      ? `?commentableType=${commentableType}`
      : '';
    return (await initAxios())
      ?.get(`/bite-db/comments/${commentableId}${queryParams}`)
      .then((resp) => {
        return {
          success: true,
          result: resp.data,
        };
      })
      .catch((err) => {
        return {
          success: false,
          result: parseErrorMsg(err),
        };
      });
  },

  /**
   * Get comments with nested replies for an entity or product
   * @param {string} commentableId - The ID of the entity or product
   * @param {string} commentableType - The type of the commentable object (DatabaseEntity or DatabaseProduct)
   * @returns {Promise<{success: boolean, result: any}>} Object containing success status and result data
   */
  getCommentsWithReplies: async (
    commentableId: string,
    commentableType?: string,
  ) => {
    const queryParams = commentableType
      ? `?commentableType=${commentableType}`
      : '';
    return (await initAxios())
      ?.get(`/bite-db/comments/${commentableId}/with-replies${queryParams}`)
      .then((resp) => {
        return {
          success: true,
          result: resp.data,
        };
      })
      .catch((err) => {
        return {
          success: false,
          result: parseErrorMsg(err),
        };
      });
  },

  /**
   * Update a comment
   * @param {string} id - The ID of the comment to update
   * @param {any} updateData - The data to update the comment with
   * @returns {Promise<{success: boolean, result: any}>} Object containing success status and result data
   */
  updateComment: async (id: string, updateData: any) =>
    (await initAxios())
      ?.put(`/bite-db/comment/${id}`, JSON.stringify(updateData))
      .then((resp) => {
        return {
          success: true,
          result: resp.data,
        };
      })
      .catch((err) => {
        return {
          success: false,
          result: parseErrorMsg(err),
        };
      }),

  /**
   * Delete a comment (soft delete)
   * @param {string} id - The ID of the comment to delete
   * @returns {Promise<{success: boolean, result: any}>} Object containing success status and result data
   */
  deleteComment: async (id: string) =>
    (await initAxios())
      ?.delete(`/bite-db/comment/${id}`)
      .then((resp) => {
        return {
          success: true,
          result: resp.data,
        };
      })
      .catch((err) => {
        return {
          success: false,
          result: parseErrorMsg(err),
        };
      }),

  getExternalTemplatesByGroupId: async (groupId: string) =>
    (await initAxios())
      ?.get(`/bite-api/externalTemplates/group/${groupId}`)
      .then((resp) => {
        return {
          success: true,
          result: resp.data.data,
        };
      })
      .catch((err) => {
        return {
          success: false,
          result: parseErrorMsg(err),
        };
      }),

  getExternalTemplateById: async (id: string) =>
    (await initAxios())
      ?.get(`/bite-api/externalTemplates/${id}`)
      .then((resp) => {
        return {
          success: true,
          result: resp.data.data,
        };
      })
      .catch((err) => {
        return {
          success: false,
          result: parseErrorMsg(err),
        };
      }),

  // Routes to the product service's CargoWise-shaped export generation
  // (POST /product/export/cargowise there) via the bite-db proxy -- see
  // server/server/api/controllers/bite_db/product-export-proxy.ts.
  sendExportViaApi: async (payload: {
    database_id: string;
    field_mapping_id: string;
  }) =>
    (await initAxios())
      ?.post('/bite-db/product-export/cargowise', payload)
      .then((resp) => {
        return {
          success: true,
          result: resp.data,
        };
      })
      .catch((err) => {
        return {
          success: false,
          result: parseErrorMsg(err),
        };
      }),

  createExternalTemplate: async (templateBody: any) =>
    (await initAxios())
      ?.post('/bite-api/externalTemplates', JSON.stringify(templateBody))
      .then((resp) => {
        return {
          success: true,
          result: resp.data.data,
        };
      })
      .catch((err) => {
        return {
          success: false,
          result: parseErrorMsg(err),
        };
      }),

  updateExternalTemplate: async (id: string, templateBody: any) =>
    (await initAxios())
      ?.put(`/bite-api/externalTemplates/${id}`, JSON.stringify(templateBody))
      .then((resp) => {
        return {
          success: true,
          result: resp.data.data,
        };
      })
      .catch((err) => {
        return {
          success: false,
          result: parseErrorMsg(err),
        };
      }),

  deleteExternalTemplate: async (id: string) =>
    (await initAxios())
      ?.delete(`/bite-api/externalTemplates/${id}`)
      .then((resp) => {
        return {
          success: true,
          result: resp.data.data,
        };
      })
      .catch((err) => {
        return {
          success: false,
          result: parseErrorMsg(err),
        };
      }),

  searchBiteApis: async (queryBody: any) =>
    (await initAxios())
      ?.post('/bite-api/search', JSON.stringify(queryBody))
      .then((resp) => {
        return {
          success: true,
          result: resp.data,
        };
      })
      .catch((err) => {
        return {
          success: false,
          result: parseErrorMsg(err),
        };
      }),

  getUniqueAdditionalColumnKeysByDatabaseId: async (databaseId: string) =>
    (await initAxios())
      ?.get(`/bite-api/additional-columns/keys/${databaseId}`)
      .then((resp) => {
        return {
          success: true,
          result: resp.data.data,
        };
      })
      .catch((err) => {
        return {
          success: false,
          result: parseErrorMsg(err),
        };
      }),
};

export default BiteDBAPI;
